import type { MFEConfig, MFEModule } from "@pepperstone/mfe-dev-kit/types";
import type React from "react";
import ReactDOM from "react-dom/client";
import { withStyleReset } from "@/components/general/style-reset/withStyleReset";

import {
  insertHostFields,
  setPaymentButtonsInShadowDom,
} from "@/utils/shadow-dom";

export const mfeRenderBuilder = (
  Component: React.FC<React.PropsWithChildren<MFEConfig>>
): Required<MFEModule> => {
  let appRoot: ReactDOM.Root | null = null;
  const render: MFEModule["render"] = async (mfeConfig: MFEConfig) => {
    const logger = mfeConfig.dependencies?.logger ?? console;

    if (!mfeConfig.dependencies) {
      throw new Error(
        "Could not render. No dependencies provided from container."
      );
    }

    if (!appRoot) {
      appRoot = ReactDOM.createRoot(mfeConfig.root);
    }

    // insert slots into `wrapper` container
    // https://github.com/pepperstone/mfe-dev-kit/blob/b46aae0d4f6438c22aa0b5002a6296e35c1bfe01/src/container/makeMFEDom.ts
    if (mfeConfig.shadowRoot) {
      const shadowHost = mfeConfig.shadowRoot.host;
      if (shadowHost) {
        insertHostFields({ shadowHost });
        setPaymentButtonsInShadowDom(true);
      } else {
        logger.warn("Unable to access Shadow DOM host", mfeConfig);
      }
    } else {
      logger.warn("Shadow DOM not found");
    }

    const ComponentWithStyleReset = withStyleReset(() => (
      <Component {...mfeConfig} />
    ));

    appRoot?.render(<ComponentWithStyleReset />);
  };

  const cleanup: MFEModule["cleanup"] = ({ dependencies }) => {
    if (!dependencies) {
      throw new Error(
        "Could not render. No dependencies provided from container."
      );
    }

    if (!appRoot) {
      throw new Error("Could not cleanup. No appRoot found.");
    }

    appRoot.unmount();
    appRoot = null;
    setPaymentButtonsInShadowDom(false);
  };

  return { render, cleanup };
};
